import { ChangeEvent, FC } from 'react';

import { getMemberGenderSymbol } from '@healthquote/utils';
import { HealthAddOn, useHealthQuoteStore } from '@store/healthquote';
import { Text, Checkbox, Button, Stack, Group } from '@ui';

interface OptionProps {
  title: string;
  addOn: HealthAddOn;
  closeDrawer: () => void;
}

const AddOnMemberOptions: FC<OptionProps> = ({ title, addOn, closeDrawer }) => {
  const { plan, toggleModalAddOn, setDraftMemberAddOns } = useHealthQuoteStore(
    (state) => ({
      plan: state.plan,
      toggleModalAddOn: state.toggleModalAddOn,
      setDraftMemberAddOns: state.setDraftMemberAddOns
    })
  );

  const addOnKey = addOn.key;

  const handleCheckboxChange = (
    e: ChangeEvent<HTMLInputElement>,
    memberIndex: number
  ) => {
    const { checked } = e.target;
    const member = plan.draftMembers.find((_m, index) => index === memberIndex);
    if (checked) {
      setDraftMemberAddOns(memberIndex, [
        ...(member?.addons || []),
        {
          key: addOnKey,
          included: checked
        }
      ]);
    } else {
      setDraftMemberAddOns(
        memberIndex,
        member?.addons?.filter((addOn) => addOn.key !== addOnKey) || []
      );
    }
  };

  const atLeastOneMemberSelected = plan.draftMembers.some(
    (member) =>
      member.addons &&
      member.addons.some((addOn) => addOn.key === addOnKey && addOn.included)
  );

  const confirmAction = (included: boolean) => {
    toggleModalAddOn(addOnKey, plan.draftMembers, included);
    closeDrawer();
  };

  return (
    <Stack>
      <Text fz={{ base: 24, sm: 28 }} fw={600}>
        {title}
      </Text>

      <Stack gap="xs">
        <Text fz={16}>Choose member(s)</Text>
        <Text size="sm" c="secondary">
          Select the members you want to include in this rider.
        </Text>
      </Stack>

      <Stack mt="md" gap="xs">
        {plan.draftMembers.map((member, memberIndex) => {
          const memberNotEligible =
            addOn.eligibleMembers &&
            !addOn.eligibleMembers.find((mem) => mem.relation === member.relation);

          if (memberNotEligible) {
            return null;
          }

          return (
            <Checkbox
              key={member.id}
              checked={
                member.addons
                  ? member?.addons?.find((addOn) => addOn.key === addOnKey)
                      ?.included || false
                  : false
              }
              onChange={(e) => handleCheckboxChange(e, memberIndex)}
              label={`${member.relation}${
                member.gender ? ` (${getMemberGenderSymbol(member.gender)}) ` : ' '
              }(${member.age} Years)`}
              color="blue"
              size="md"
            />
          );
        })}
      </Stack>

      <Group mt="lg" w="100%">
        {atLeastOneMemberSelected ? (
          <Button
            fullWidth
            variant="filled"
            size="xl"
            disabled={!atLeastOneMemberSelected}
            onClick={() => confirmAction(true)}
          >
            Confirm
          </Button>
        ) : (
          <Button
            variant="outline"
            fullWidth
            size="xl"
            color="secondary"
            onClick={() => confirmAction(false)}
          >
            Close
          </Button>
        )}
      </Group>
    </Stack>
  );
};

export { AddOnMemberOptions };
